import { useEffect } from 'react'
import { Calendar, CheckCircle } from 'lucide-react'
import {
  useGetDashboardStatsQuery,
  useLazyGetProfileQuery,
  useGetSlaSummaryQuery,
} from '../../store/api/apiSlice'
import { UserRole } from '@/types'
import OngoingInvestigationTable from '../../components/OngoingInvestigationTable'
import CompletedInvestigationTable from '../../components/CompletedInvestigationTable'
import TeamDisplay from '../../components/TeamDisplay'
import EmailVerificationBanner from '../../components/EmailVerificationBanner'
import SlaSummaryCards from '../../components/SlaSummaryCards'
import { useAppDispatch, useAppSelector } from '../../hooks/useAppStore'
import { setUser, selectCurrentUser } from '../../store/slices/authSlice'

export default function Dashboard() {
  const dispatch = useAppDispatch()
  const currentUser = useAppSelector(selectCurrentUser)
  const isSuperAdmin = currentUser?.role === UserRole.SUPER_ADMIN
  const { data: stats, isLoading, error } = useGetDashboardStatsQuery()
  const [getProfile] = useLazyGetProfileQuery()
  // Admin-only; skip the request entirely for other roles so non-admins don't
  // hit a 403 and see a spurious error in the console.
  const { data: slaSummary } = useGetSlaSummaryQuery(undefined, { skip: !isSuperAdmin })

  // Refresh user profile on mount to ensure we have the latest verification status
  useEffect(() => {
    const refreshProfile = async () => {
      try {
        const profileData = await getProfile().unwrap()
        dispatch(setUser(profileData))
      } catch (error) {
        // Silently fail - profile refresh is not critical for dashboard display
        console.error('Failed to refresh profile on dashboard mount:', error)
      }
    }
    refreshProfile()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount

  // Default values while loading
  const investigations = stats?.investigations || { total: 0, ongoing: 0, completed: 0, pending: 0, new: 0 }

  if (error) {
    return (
      <main className='w-full min-h-screen lg:h-screen flex items-center justify-center px-4 py-4 md:py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]'>
        <div className='text-center'>
          <h2 className='text-2xl font-bold text-gray-900 mb-2'>Error Loading Dashboard</h2>
          <p className='text-gray-600'>Please try refreshing the page</p>
        </div>
      </main>
    )
  }

  return (
    <>
      {/* Email Verification Banner */}
      <EmailVerificationBanner />

      <main className='w-full min-h-screen lg:h-screen flex flex-col px-4 py-4 md:py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]'>
        {/* Top Section - Welcome and Stats */}
        <div className='bg-white rounded-2xl md:rounded-[32px] px-4 md:px-8 lg:px-16 py-6 md:py-10 lg:py-12 mb-4 md:mb-6 shadow-sm relative flex-shrink-0'>
          <div className='flex flex-col lg:flex-row items-start justify-between gap-4 lg:gap-6'>
            <div className='flex-1 w-full'>
              {/* Welcome Section */}
              <div className='mb-6 md:mb-12'>
                <p className='text-gray-400 text-sm md:text-base mb-2 text-start'>Good day</p>
                <h1 className='text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 text-start'>
                  Let's get more done today
                </h1>
              </div>

              {/* Stats Section - Horizontal Layout */}
              <div className='flex flex-col sm:flex-row flex-wrap items-start gap-6 md:gap-8 lg:gap-10'>
                {/* Ongoing Investigations */}
               <div className='flex flex-col bg-[#F5F5F5] p-4 rounded-lg'>
                 <div className='flex items-start gap-2 md:gap-3'>
                  <Calendar className='w-5 h-5 md:w-6 md:h-6 text-[#FF6B35] mt-2' />
                  <div>
                    <h3 className='text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-1 text-start'>
                      {isLoading ? '...' : investigations.ongoing}
                    </h3>
                  </div>
                </div>
                <p className='text-xs md:text-sm text-gray-600 text-start mt-2'>Ongoing Investigations</p>
               </div>

 <div className='flex flex-col bg-[#F5F5F5] p-4 rounded-lg'>
                {/* Completed Investigations */}
                <div className='flex items-start gap-2 md:gap-3'>
                <CheckCircle className='w-5 h-5 md:w-6 md:h-6 text-[#FF6B35] mt-2' />
                  <div>
                    <h3 className='text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-1'>
                      {isLoading ? '...' : investigations.completed}
                    </h3>
                  </div>
                </div>
                    <p className='text-xs md:text-sm text-gray-600'>Completed Investigations</p>
 </div>
              </div>
            </div>

            {/* Team Display - Right Side */}
            <TeamDisplay />
          </div>
        </div>

        {/* SLA Summary (super-admin only) */}
        {isSuperAdmin && slaSummary && (
          <div className='mb-4 md:mb-6 flex-shrink-0'>
            <SlaSummaryCards summary={slaSummary} />
          </div>
        )}

        {/* Tables Section */}
        <div className='flex-1 min-h-0 overflow-auto pb-4'>
          <div className='grid grid-cols-1 2xl:grid-cols-2 gap-4 md:gap-6 h-full'>
            {/* Ongoing Investigation Table */}
            <div className='w-full h-full min-h-[500px]'>
              <OngoingInvestigationTable />
            </div>

            {/* Completed Investigation Table */}
            <div className='w-full h-full min-h-[500px]'>
              <CompletedInvestigationTable />
            </div>
          </div>
        </div>
      </main>
    </>
  )
}
